import { RemoveSymbolsByMaskActionHandler, type ReplayPlugin, type RoundEvent } from "@slot/client-engine";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import type { BaseGridPresenter, GridPresenter, MechanicPlugin } from "@slot/mechanics-core";
import type { PreSpinDropOutBoundaryHook } from "./PreSpinDropOutBoundaryHook";
import { GridPreSpinDrop } from "./GridPreSpinDrop";
import { GridReveal } from "./GridReveal";

/**
 * The cascade as a mechanic plugin: the frame presenter starts the boundary drop-out without waiting
 * for the fall, then lets the base layer prepare (its reveal waits on the "spin" barrier the drop-out
 * releases as its last symbol starts to fall) and reveals through the composite grid presenter; the
 * removal handler plays the removal cue, shrinks through the composite, then holds a beat on engine
 * time before the collapse event drives the drop.
 */
export function buildCascadeReplayPlugin(input: {
  gridPresenter: GridPresenter;
  basePresenter: BaseGridPresenter;
  preSpinBoundaryHook: PreSpinDropOutBoundaryHook;
  /** Drives the post-removal hold on engine time. */
  runner: PresentationTaskRunner;
  postRemovalHoldSeconds: number;
  /** The removal cue (the game plays its sound and haptic); omit to play nothing. */
  onRemoveSymbols?: () => void;
  log?: Log;
}): MechanicPlugin {
  const log = input.log ?? (() => {});
  const plugin: ReplayPlugin = {
    name: "cascade",
    actionHandlers: [
      new RemoveSymbolsByMaskActionHandler(async (positionMask) => {
        log(`[Remove] ${positionMask}`);
        input.onRemoveSymbols?.();
        // Awaits the longest of the base layer's shrink and the win highlight's un-dim fade (the
        // composite runs them in parallel); only then does the hold begin.
        await input.gridPresenter.removeSymbolsByMask(positionMask);
        if (input.postRemovalHoldSeconds > 0) {
          await input.runner.sleep(input.postRemovalHoldSeconds);
        }
      }),
    ],
  };
  return {
    framePresenter: {
      prepareEventFrame: async (event) => {
        input.preSpinBoundaryHook.onSpinBoundaryStart({ spinModeIdentifier: extractSpinModeIdentifier(event) });
        await input.basePresenter.prepareEventFrame(event);
      },
      revealEventFrame: async (event) => {
        log(`[Frame] reveal ${event.eventTypeName}`);
        await input.gridPresenter.presentEventFrame(event);
      },
    },
    plugin,
    slamScopes: { preSpin: GridPreSpinDrop.SCOPE, presentation: [GridReveal.SCOPE] },
  };
}

/** The `spinModeIdentifier` of the event's `spin_boundary_start` action, if it carries one. */
function extractSpinModeIdentifier(event: RoundEvent): string | undefined {
  const action = event.eventContent.orderedActions?.find((a) => a.actionTypeName === "spin_boundary_start");
  const payload = action?.actionPayload as { spinModeIdentifier?: unknown } | undefined;
  return typeof payload?.spinModeIdentifier === "string" ? payload.spinModeIdentifier : undefined;
}
